import type { IFieldDefinition, IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { columnsFor, defaultColumns, fieldsFor } from '@platform/core/lib/viewSettings';
import { childRelationsOf } from '../childRelations';
import type { ISubTableDef } from './ISubTableDef';
import { subTableFlagOf } from './subTableFlagOf';

/** Найбільше колонок підтаблиці без явного переліку (прототип SUB_DEFS — 2–4). */
const MAX_COLUMNS = 4;

/**
 * Колонки без явного переліку: власна назва типу (без автоназви) першою, далі подання inline → колонки таблиці, без поля-посилання на
 * батька й статусу.
 */
const defaultColumnsOf = (child: IRecordTypeDefinition, lookup: IFieldDefinition): string[] => {
  const title = !child.titleAuto && child.fields.some((f) => f.key === 'title') ? ['title'] : [];
  const rest = fieldsFor(child, 'inline', columnsFor(child, 'table', defaultColumns(child))).filter((k) => k !== lookup.key && k !== 'status' && k !== 'title');
  return [...title, ...rest].slice(0, MAX_COLUMNS);
};

/**
 * Підтаблиці другої секції «Подробиці» батьківського типу (прототип v4 SUB_DEFS; UIUX forms/form-structure): лише дочірні типи модуля з
 * полем-посиланням на цей тип і ознакою підтаблиці (`subTable`), за порядком ознаки; колонки — з ознаки (лише поля, що є в типі), інакше
 * типові. Без ознаки підтаблиць немає (прототип: дочірні записи законопроєкту, аналізу, інтерв'ю — не на «Загальному»). Чиста функція.
 */
export function subTablesOf(module: IModuleDefinition, rt: IRecordTypeDefinition): ISubTableDef[] {
  const flagged = childRelationsOf(module, rt).map((r) => ({ ...r, flag: subTableFlagOf(r.child) })).filter((r) => r.flag);
  const chosen = flagged.sort((a, b) => (a.flag?.order ?? 0) - (b.flag?.order ?? 0));
  return chosen.map(({ child, lookup, flag }) => {
    const keys = flag?.columns?.length ? flag.columns : defaultColumnsOf(child, lookup);
    const columns = keys.map((k) => child.fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => Boolean(f) && f?.key !== lookup.key);
    return { key: child.key, child, lookup, label: flag?.label, columns };
  });
}
